import React from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, User, LogOut } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  // Check if in prelaunch mode
  const isPrelaunchMode = process.env.REACT_APP_PRELAUNCH_MODE === 'true';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get user initials
  const getUserInitials = (user) => {
    if (!user || !user.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return names[0][0].toUpperCase();
  };

  // Handle logout
  const handleLogout = async () => {
    setIsDropdownOpen(false);
    await logout();
    navigate('/');
  };

  // Handle dashboard navigation
  const handleDashboard = () => {
    setIsDropdownOpen(false);
    if (user?.role === 'provider') {
      navigate('/provider-dashboard');
    } else if (user?.role === 'admin') {
      navigate('/admin');
    } else {
      navigate('/customer-dashboard');
    }
  };

  return (
    <header className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold text-emerald-600 hover:text-emerald-700 transition-colors duration-200">
              FixDirectly
            </Link>
          </div>

          {/* Desktop Navigation - hidden in prelaunch mode */}
          {!isPrelaunchMode && (
            <nav className="hidden lg:flex items-center space-x-8">
              <a href="/#how-it-works" className="whitespace-nowrap text-gray-700 hover:text-emerald-600 transition-colors duration-200">
                How It Works
              </a>
              <a href="/services" className="text-gray-700 hover:text-emerald-600 transition-colors duration-200">
                Services
              </a>
              <a href="/#benefits" className="text-gray-700 hover:text-emerald-600 transition-colors duration-200">
                Benefits
              </a>
              <a href="/#faq" className="text-gray-700 hover:text-emerald-600 transition-colors duration-200">
                FAQ
              </a>
              <Link to="/blog" className="text-gray-700 hover:text-emerald-600 transition-colors duration-200">
                Blog
              </Link>
              <Link
                to="/group-buy"
                className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3.5 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 transition-colors duration-200"
              >
                Group Buy Now
                <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] font-bold uppercase leading-none tracking-wide">
                  New
                </span>
              </Link>
              <Link to="/contact" className="text-gray-700 hover:text-emerald-600 transition-colors duration-200">
                Contact
              </Link>
            </nav>
          )}

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            {isAuthenticated && (!isPrelaunchMode || user?.role === 'admin') ? (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  className="flex items-center space-x-2 bg-emerald-600 text-white px-3 py-2 rounded-lg hover:bg-emerald-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                >
                  <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-sm font-semibold">
                    {getUserInitials(user)}
                  </div>
                  <span className="text-sm font-medium">{user?.name?.split(' ')[0] || 'User'}</span>
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-50">
                    <button
                      onClick={handleDashboard}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <User className="w-4 h-4" />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-150"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link to="/signin">
                  <Button variant="ghost" className="text-gray-700 hover:text-emerald-600">
                    Log in
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg transition-all duration-200 transform hover:scale-105">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button - hidden in prelaunch mode */}
          {!isPrelaunchMode && (
            <div className="lg:hidden flex items-center gap-2">
              {/* Always-visible Group Buy Now pill (no menu tap needed). */}
              <Link
                to="/group-buy"
                className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors"
              >
                Group Buy
                <span className="rounded-full bg-white/25 px-1 py-0.5 text-[9px] font-bold uppercase leading-none tracking-wide">New</span>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label={isMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                aria-expanded={isMenuOpen}
              >
                {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
              </Button>
            </div>
          )}
          
          {/* Mobile auth buttons in prelaunch mode */}
          {isPrelaunchMode && (
            <div className="lg:hidden flex items-center space-x-2">
              <Link to="/signin">
                <Button variant="ghost" size="sm" className="text-gray-700">
                  Log in
                </Button>
              </Link>
              <Link to="/signup">
                <Button size="sm" className="bg-emerald-600 text-white">
                  Get Started
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Mobile Navigation - hidden in prelaunch mode */}
        {isMenuOpen && !isPrelaunchMode && (
          <div className="lg:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-100">
              <a href="/#how-it-works" className="block px-3 py-2 text-gray-700 hover:text-emerald-600">
                How It Works
              </a>
              <a href="/services" className="block px-3 py-2 text-gray-700 hover:text-emerald-600">
                Services
              </a>
              <a href="/#benefits" className="block px-3 py-2 text-gray-700 hover:text-emerald-600">
                Benefits
              </a>
              <a href="/#faq" className="block px-3 py-2 text-gray-700 hover:text-emerald-600">
                FAQ
              </a>
              <Link to="/blog" className="block px-3 py-2 text-gray-700 hover:text-emerald-600">
                Blog
              </Link>
              <Link to="/group-buy" className="mx-3 my-1 flex items-center justify-between rounded-lg bg-emerald-600 px-3 py-2 font-semibold text-white hover:bg-emerald-700">
                Group Buy Now
                <span className="rounded-full bg-white/25 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide">New</span>
              </Link>
              <Link to="/contact" className="block px-3 py-2 text-gray-700 hover:text-emerald-600">
                Contact
              </Link>
              <div className="flex flex-col space-y-2 px-3 pt-2">
                {isAuthenticated ? (
                  <>
                    <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg mb-2">
                      <div className="w-10 h-10 bg-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                        {getUserInitials(user)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                    </div>
                    <button
                      onClick={handleDashboard}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                    >
                      <User className="w-4 h-4" />
                      <span>Dashboard</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors duration-150"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/signin">
                      <Button variant="ghost" className="justify-start w-full">
                        Log in
                      </Button>
                    </Link>
                    <Link to="/signup">
                      <Button className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        Get Started
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
